import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { formatDate } from '../../date-utils';
import { useRoomAvailabilityData } from '../../hooks/use-room-availability-data';

export function RoomAvailabilityTable() {
	const { tableRows, availableDates, loading, error } =
		useRoomAvailabilityData();

	// Helper function to determine the availability class
	const getAvailabilityClass = (count: number) => {
		if (count <= 2) return 'tw-text-destructive tw-font-semibold';
		if (count <= 5) return 'tw-text-amber-500 tw-font-medium';
		return '';
	};

	if (loading) {
		return (
			<div className='tw-mt-6 tw-mb-8 tw-overflow-x-auto tw-rounded-md tw-border tw-shadow-sm'>
				<div className='tw-bg-muted/30 tw-p-3 tw-border-b'>
					<h3 className='tw-text-lg tw-font-medium'>Room Type Availability</h3>
				</div>
				<div className='tw-p-4 tw-text-center'>Loading data...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='tw-mt-6 tw-mb-8 tw-overflow-x-auto tw-rounded-md tw-border tw-shadow-sm'>
				<div className='tw-bg-muted/30 tw-p-3 tw-border-b'>
					<h3 className='tw-text-lg tw-font-medium'>Room Type Availability</h3>
				</div>
				<div className='tw-p-6 tw-text-destructive'>
					Error loading room availability data
				</div>
			</div>
		);
	}

	if (!tableRows.length || !availableDates.length) {
		return (
			<div className='tw-mt-6 tw-mb-8 tw-overflow-x-auto tw-rounded-md tw-border tw-shadow-sm'>
				<div className='tw-bg-muted/30 tw-p-3 tw-border-b'>
					<h3 className='tw-text-lg tw-font-medium'>Room Type Availability</h3>
				</div>
				<div className='tw-p-6 tw-text-center tw-text-muted-foreground'>
					No room availability data for the selected dates
				</div>
			</div>
		);
	}

	return (
		<div className='tw-mt-6 tw-mb-8 tw-overflow-x-auto tw-rounded-md tw-border tw-shadow-sm'>
			<div className='tw-bg-secondary tw-p-3 tw-border-b'>
				<h3 className='tw-text-lg tw-font-medium'>Room Type Availability</h3>
			</div>
			<Table>
				<TableHeader>
					<TableRow className='tw-bg-white hover:tw-bg-secondary'>
						<TableHead className='tw-w-[100px] tw-font-semibold'>
							Room Type
						</TableHead>
						<TableHead className='tw-w-[200px] tw-font-semibold'>
							Description
						</TableHead>
						{availableDates.map((date) => (
							<TableHead key={date} className='tw-font-semibold'>
								{formatDate(date)}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{tableRows.map((row, index) => {
						const isSubRow = !row.roomType && row.description;
						return (
							<TableRow
								key={index}
								className={`tw-bg-white hover:tw-bg-secondary ${isSubRow ? 'tw-pl-6' : ''}`}
							>
								<TableCell className='tw-font-medium tw-text-nowrap tw-overflow-x-auto'>
									{row.roomType}
								</TableCell>
								<TableCell className='tw-text-nowrap tw-overflow-x-auto'>
									{row.description}
								</TableCell>
								{availableDates.map((date) => {
									const count = row.dateValues[date] ?? 0;
									return (
										<TableCell
											key={date}
											className={getAvailabilityClass(Number(count))}
										>
											{count || '-'}
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
